import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Round 4 (owner, 2026-09-30, items 10a and 10c): the page state of a list the server pages on a
 * keyset cursor (the Runs list, a connection's imports, a run's events).
 *
 * Previous is a stack of the cursors the server issued, never arithmetic: a keyset cursor names
 * "the rows after this one", so a page number cannot be turned into one, and a run that lands
 * between two reads never repeats or skips a row the way an OFFSET would. The client never builds
 * a cursor; it hands back exactly the `meta.nextCursor` it was given.
 *
 * A new filter set (`resetKey`) or a new page size starts again at the first page: the stack's
 * cursors belong to the old query. Adjusted during render, not in an effect, so no frame ever
 * reads the new filters with an old cursor.
 */
export interface CursorPagerState {
  /** The cursor of the page on screen; null on the first page. */
  cursor: string | null;
  /** 0 on the first page. */
  pageIndex: number;
  isFirstPage: boolean;
  pageSize: number;
  pageSizes: readonly number[];
  next: (cursor: string) => void;
  previous: () => void;
  /** Back to the first (newest) page. */
  first: () => void;
  setPageSize: (size: number) => void;
}

export function useCursorPager({
  pageSizes,
  defaultPageSize,
  resetKey,
}: {
  pageSizes: readonly number[];
  defaultPageSize: number;
  /** Any value that changes when the filters change (the query string, a connection id). */
  resetKey: string;
}): CursorPagerState {
  const [state, setState] = useState<{
    key: string;
    stack: Array<string | null>;
    pageSize: number;
  }>({ key: resetKey, stack: [null], pageSize: defaultPageSize });
  if (state.key !== resetKey) setState({ ...state, key: resetKey, stack: [null] });
  const stack = state.key === resetKey ? state.stack : [null];

  const next = useCallback(
    (cursor: string) => setState((s) => ({ ...s, stack: [...s.stack, cursor] })),
    [],
  );
  const previous = useCallback(
    () => setState((s) => (s.stack.length > 1 ? { ...s, stack: s.stack.slice(0, -1) } : s)),
    [],
  );
  const first = useCallback(
    () => setState((s) => (s.stack.length > 1 ? { ...s, stack: [null] } : s)),
    [],
  );
  const setPageSize = useCallback(
    (size: number) => {
      if (!pageSizes.includes(size)) return;
      setState((s) => (s.pageSize === size ? s : { ...s, pageSize: size, stack: [null] }));
    },
    [pageSizes],
  );

  return {
    cursor: stack[stack.length - 1] ?? null,
    pageIndex: stack.length - 1,
    isFirstPage: stack.length === 1,
    pageSize: state.pageSize,
    pageSizes,
    next,
    previous,
    first,
    setPageSize,
  };
}

/**
 * Where the page's rows sit in the whole list, 1-based: "21-40". `newer` is how many rows arrived
 * above them since the reader left the first page (a later page never shifts, the keyset keeps
 * it, so the rows moved down by that many). No rows is 0-0.
 */
export function pagerRange({
  pageIndex,
  pageSize,
  rowCount,
  newer = 0,
}: {
  pageIndex: number;
  pageSize: number;
  rowCount: number;
  newer?: number;
}): { start: number; end: number } {
  if (rowCount <= 0) return { start: 0, end: 0 };
  const start = pageIndex * pageSize + Math.max(0, newer) + 1;
  return { start, end: start + rowCount - 1 };
}

/**
 * Next, read before the pager moves (round 4): `fetchPage` reads the next page into the cache and
 * only then does the pager push its cursor, so a failed read leaves the page the reader was on
 * (NFR-8) and a read that works lands on a warm cache. The push is dropped when the filters or
 * the size changed while the page was read (`pageKey`): the cursor belongs to the old query.
 *
 * It also counts the rows that arrived above a later page (`newer`): the total when the reader
 * left the first page, against the total the page's latest read says. The keyset keeps a later
 * page's rows still, so the difference is exactly the rows now above them.
 */
export function useNextPage({
  pager,
  pageKey,
  total,
  nextCursor,
  fetchPage,
}: {
  pager: Pick<CursorPagerState, "cursor" | "isFirstPage" | "next">;
  /** Changes with the filters and the page size. */
  pageKey: string;
  total: number | null;
  nextCursor: string | null;
  fetchPage: (cursor: string) => Promise<unknown>;
}): { goNext: () => Promise<void>; pending: boolean; error: Error | null; newer: number } {
  const currentKey = useRef(pageKey);
  useEffect(() => {
    currentKey.current = pageKey;
  });
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{
    key: string;
    at: string | null;
    error: Error;
  } | null>(null);
  const [leftFirstPage, setLeftFirstPage] = useState<{ key: string; total: number } | null>(null);

  const goNext = async (): Promise<void> => {
    if (!nextCursor || pending) return;
    const askedFrom = { key: pageKey, at: pager.cursor };
    const firstTotal = pager.isFirstPage ? total : null;
    setPending(true);
    setFailure(null);
    try {
      await fetchPage(nextCursor);
      if (currentKey.current !== askedFrom.key) return;
      if (firstTotal !== null) setLeftFirstPage({ key: askedFrom.key, total: firstTotal });
      pager.next(nextCursor);
    } catch (error) {
      if (currentKey.current === askedFrom.key) {
        setFailure({
          ...askedFrom,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    } finally {
      setPending(false);
    }
  };

  return {
    goNext,
    pending,
    error: failure?.key === pageKey && failure.at === pager.cursor ? failure.error : null,
    newer:
      !pager.isFirstPage && leftFirstPage?.key === pageKey && total !== null
        ? Math.max(0, total - leftFirstPage.total)
        : 0,
  };
}
